import { DataTable } from "@/components/data-table/DataTable";
import { createFileRoute } from "@tanstack/react-router";
import type { AdminOrganizationFamilyBalanceDTO } from "@/orval/model";
import type { ColumnDef } from "@tanstack/react-table";
import { Form } from "@/components/form/form";
import { CheckboxFormField } from "@/components/form/form-field/checkbox-form-field";
import { OrganizationSemestersComboboxFormField } from "@/components/form/form-field/combobox/organization-semesters-combobox-form-field";
import {
  getAdminOrganizationGetCurrentAndActiveSemesterQueryOptions,
  useAdminOrganizationGetCurrentAndActiveSemester,
} from "@/orval/admin-organization";
import { adminFamilyBalancesGetFamilyBalances } from "@/orval/admin-family-balances";
import { formatCurrency } from "@/lib/currency-utils";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PlusIcon } from "lucide-react";
import { PaymentDialog } from "./-components/payment-dialog";
import { PortalPageHeader } from "@/components/portal-page-header";
import { z } from "zod";
import { getAdminFamilyMembersLink } from "./-components/family-navigation";
import { buildDataTableQueryOptions, ensureRouteQueriesData } from "@/lib/route-loader";

const familyBalancesSearchSchema = z.object({
  semesterId: z.coerce.number().optional(),
  includeInactive: z.union([z.boolean(), z.stringbool()]).optional(),
});

export const Route = createFileRoute("/_app/organizations/$organizationId/admin/family-balances")({
  component: RouteComponent,
  validateSearch: familyBalancesSearchSchema,
  loader: async ({ params, location, ...loaderContext }) => {
    const search = familyBalancesSearchSchema.parse(location.search);
    let semesterId = search.semesterId;
    if (semesterId === undefined) {
      const semesters = await loaderContext.context.queryClient.ensureQueryData(
        getAdminOrganizationGetCurrentAndActiveSemesterQueryOptions(params.organizationId),
      );
      semesterId = semesters?.nextSemesterId ?? semesters?.activeSemesterId ?? undefined;
    }

    if (!semesterId) {
      return;
    }

    const includeInactive = search.includeInactive ?? false;
    const queryOptions = buildDataTableQueryOptions({});
    return ensureRouteQueriesData(loaderContext, [
      {
        queryKey: ["family-balances", params.organizationId, semesterId, includeInactive, queryOptions, []],
        queryFn: () =>
          adminFamilyBalancesGetFamilyBalances(params.organizationId, semesterId, [], {
            ...queryOptions,
            includeInactive,
          }),
      },
    ]);
  },
});

const columns: ColumnDef<AdminOrganizationFamilyBalanceDTO>[] = [
  {
    header: "Family Name",
    accessorKey: "familyName",
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <span>{row.original.familyName}</span>
        {!row.original.isActive && <Badge variant="secondary">Inactive</Badge>}
      </div>
    ),
  },
  {
    header: "Amount Due",
    accessorFn: (row) => formatCurrency(row.amountDue),
  },
  {
    header: "Amount Paid",
    accessorFn: (row) => formatCurrency(row.amountPaid),
  },
  {
    header: "Venmo Selected",
    accessorFn: (row) => (row.useVenmo ? "Yes" : "No"),
  },
  {
    header: "Venmo Fee",
    accessorFn: (row) => (row.useVenmo ? formatCurrency(row.venmoFee) : "—"),
  },
  {
    header: "Balance",
    accessorFn: (row) => formatCurrency(row.balance),
  },
];

function RouteComponent() {
  const { organizationId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState<number | undefined>(undefined);

  const { data: currentAndNextSemester } = useAdminOrganizationGetCurrentAndActiveSemester(organizationId);

  const semesterId =
    (search.semesterId ?? currentAndNextSemester?.nextSemesterId ?? currentAndNextSemester?.activeSemesterId) ||
    undefined;
  const includeInactive = search.includeInactive ?? false;

  const defaultFormValues = {
    ...search,
    semesterId,
    includeInactive,
  };

  const getFamilyBalances = adminFamilyBalancesGetFamilyBalances;

  return (
    <div className="flex flex-1 min-h-0 flex-col p-4 md:p-6">
      <PortalPageHeader
        className="mb-8"
        title="Family Balances"
        actions={
          <Button onClick={() => setIsPaymentDialogOpen(true)}>
            <PlusIcon className="size-4 mr-2" />
            Add Payment
          </Button>
        }
      />

      <DataTable
        columns={columns}
        renderFilters={
          <Form<{ semesterId?: number; includeInactive: boolean }>
            useFormProps={{ defaultValues: defaultFormValues }}
            onChange={(data) => navigate({ to: ".", search: data })}
            containerClassName="flex w-full flex-col gap-2 md:w-auto md:flex-row md:items-center md:gap-4 md:gap-y-0"
          >
            <OrganizationSemestersComboboxFormField
              className="w-full md:w-80"
              name="semesterId"
              organizationId={organizationId}
            />
            <CheckboxFormField name="includeInactive" label="Include inactive families" className="md:ml-2 md:mr-4" />
          </Form>
        }
        missingRequiredFilterValues={!semesterId}
        missingRequiredFilterValuesMessage="Please select a semester."
        queryFn={(queryOptions, signal, filters) => {
          return getFamilyBalances(
            organizationId,
            semesterId!,
            filters ?? [],
            { ...queryOptions, includeInactive },
            signal,
          );
        }}
        queryKeyFn={(params, filters) => [
          "family-balances",
          organizationId,
          semesterId!,
          includeInactive,
          params,
          filters,
        ]}
        rowLink={(row) => getAdminFamilyMembersLink(organizationId, row.organizationFamilyId)}
        getRowIdFn={(row) => row.organizationFamilyId.toString()}
      />

      {semesterId && (
        <PaymentDialog
          organizationId={organizationId}
          semesterId={semesterId}
          isOpen={isPaymentDialogOpen}
          onClose={() => {
            setIsPaymentDialogOpen(false);
            setSelectedPaymentId(undefined);
          }}
          paymentId={selectedPaymentId}
        />
      )}
    </div>
  );
}
